import { createSelector } from '@reduxjs/toolkit';
import { WorkspaceState } from './workspace.types';
import { IWorkspace } from '../../types';

// Define the root state interface
interface RootState {
  workspace: WorkspaceState;
}

// Base selector for workspace state
const selectWorkspaceState = (state: RootState): WorkspaceState =>
  state.workspace;

// Workspace data selectors
export const selectAllWorkspaces = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): IWorkspace[] => workspaceState.workspaces
);

export const selectAdminWorkspaces = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): IWorkspace[] =>
    workspaceState.adminWorkspaces
);

export const selectArchivedWorkspaces = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): IWorkspace[] =>
    workspaceState.archivedWorkspaces
);

export const selectWorkspaceData = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): IWorkspace | null =>
    workspaceState.workspace
);

// Loading state selectors
export const selectWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean => workspaceState.loading
);

export const selectGetAllWorkspacesLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getAllWorkspacesLoading
);

export const selectGetAdminWorkspacesLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getAdminWorkspacesLoading
);

export const selectGetArchivedWorkspacesLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getArchivedWorkspacesLoading
);

export const selectArchiveWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.archiveWorkspaceLoading
);

export const selectGetWorkspaceByIdLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getWorkspaceByIdLoading
);

export const selectCreateWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.createWorkspaceLoading
);

export const selectUpdateWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.updateWorkspaceLoading
);

export const selectDeleteWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.deleteWorkspaceLoading
);

export const selectSwitchWorkspaceLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.switchWorkspaceLoading
);

export const selectRemoveUserLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean => workspaceState.removeUserLoading
);

export const selectUpdateWorkspaceSettingsLoading = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.updateWorkspaceSettingsLoading
);

// Error state selectors
export const selectWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null => workspaceState.error
);

export const selectGetAllWorkspacesError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.getAllWorkspacesError
);

export const selectGetAdminWorkspacesError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.getAdminWorkspacesError
);

export const selectGetArchivedWorkspacesError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.getArchivedWorkspacesError
);

export const selectArchiveWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.archiveWorkspaceError
);

export const selectGetWorkspaceByIdError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.getWorkspaceByIdError
);

export const selectCreateWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.createWorkspaceError
);

export const selectUpdateWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.updateWorkspaceError
);

export const selectDeleteWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.deleteWorkspaceError
);

export const selectSwitchWorkspaceError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.switchWorkspaceError
);

export const selectRemoveUserError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.removeUserError
);

export const selectUpdateWorkspaceSettingsError = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.updateWorkspaceSettingsError
);

// Success state selectors
export const selectWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null => workspaceState.success
);

export const selectGetAllWorkspacesSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getAllWorkspacesSuccess
);

export const selectGetAdminWorkspacesSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getAdminWorkspacesSuccess
);

export const selectGetArchivedWorkspacesSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getArchivedWorkspacesSuccess
);

export const selectArchiveWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.archiveWorkspaceSuccess
);

export const selectGetWorkspaceByIdSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.getWorkspaceByIdSuccess
);

export const selectCreateWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.createWorkspaceSuccess
);

export const selectUpdateWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.updateWorkspaceSuccess
);

export const selectDeleteWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.deleteWorkspaceSuccess
);

export const selectSwitchWorkspaceSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean =>
    workspaceState.switchWorkspaceSuccess
);

export const selectRemoveUserSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): boolean => workspaceState.removeUserSuccess
);

export const selectUpdateWorkspaceSettingsSuccess = createSelector(
  [selectWorkspaceState],
  (workspaceState: WorkspaceState): string | null =>
    workspaceState.updateWorkspaceSettingsSuccess
);

// Combined selectors
export const selectWorkspaceLoadingState = createSelector(
  [
    selectGetAllWorkspacesLoading,
    selectGetAdminWorkspacesLoading,
    selectGetArchivedWorkspacesLoading,
    selectArchiveWorkspaceLoading,
    selectGetWorkspaceByIdLoading,
    selectCreateWorkspaceLoading,
    selectUpdateWorkspaceLoading,
    selectDeleteWorkspaceLoading,
    selectSwitchWorkspaceLoading,
    selectRemoveUserLoading,
  ],
  (
    getAllWorkspacesLoading: boolean,
    getAdminWorkspacesLoading: boolean,
    getArchivedWorkspacesLoading: boolean,
    archiveWorkspaceLoading: boolean,
    getWorkspaceByIdLoading: boolean,
    createWorkspaceLoading: boolean,
    updateWorkspaceLoading: boolean,
    deleteWorkspaceLoading: boolean,
    switchWorkspaceLoading: boolean,
    removeUserLoading: boolean
  ) => ({
    getAllWorkspacesLoading,
    getAdminWorkspacesLoading,
    getArchivedWorkspacesLoading,
    archiveWorkspaceLoading,
    getWorkspaceByIdLoading,
    createWorkspaceLoading,
    updateWorkspaceLoading,
    deleteWorkspaceLoading,
    switchWorkspaceLoading,
    removeUserLoading,
    anyLoading:
      getAllWorkspacesLoading ||
      getAdminWorkspacesLoading ||
      getArchivedWorkspacesLoading ||
      archiveWorkspaceLoading ||
      getWorkspaceByIdLoading ||
      createWorkspaceLoading ||
      updateWorkspaceLoading ||
      deleteWorkspaceLoading ||
      switchWorkspaceLoading ||
      removeUserLoading,
  })
);

export const selectWorkspaceErrorState = createSelector(
  [
    selectGetAllWorkspacesError,
    selectGetAdminWorkspacesError,
    selectGetArchivedWorkspacesError,
    selectArchiveWorkspaceError,
    selectGetWorkspaceByIdError,
    selectCreateWorkspaceError,
    selectUpdateWorkspaceError,
    selectDeleteWorkspaceError,
    selectSwitchWorkspaceError,
    selectRemoveUserError,
  ],
  (
    getAllWorkspacesError: string | null,
    getAdminWorkspacesError: string | null,
    getArchivedWorkspacesError: string | null,
    archiveWorkspaceError: string | null,
    getWorkspaceByIdError: string | null,
    createWorkspaceError: string | null,
    updateWorkspaceError: string | null,
    deleteWorkspaceError: string | null,
    switchWorkspaceError: string | null,
    removeUserError: string | null
  ) => ({
    getAllWorkspacesError,
    getAdminWorkspacesError,
    getArchivedWorkspacesError,
    archiveWorkspaceError,
    getWorkspaceByIdError,
    createWorkspaceError,
    updateWorkspaceError,
    deleteWorkspaceError,
    switchWorkspaceError,
    removeUserError,
    hasError: !!(
      getAllWorkspacesError ||
      getAdminWorkspacesError ||
      getArchivedWorkspacesError ||
      archiveWorkspaceError ||
      getWorkspaceByIdError ||
      createWorkspaceError ||
      updateWorkspaceError ||
      deleteWorkspaceError ||
      switchWorkspaceError ||
      removeUserError
    ),
  })
);

export const selectWorkspaceSuccessState = createSelector(
  [
    selectGetAllWorkspacesSuccess,
    selectGetAdminWorkspacesSuccess,
    selectGetArchivedWorkspacesSuccess,
    selectArchiveWorkspaceSuccess,
    selectGetWorkspaceByIdSuccess,
    selectCreateWorkspaceSuccess,
    selectUpdateWorkspaceSuccess,
    selectDeleteWorkspaceSuccess,
    selectSwitchWorkspaceSuccess,
    selectRemoveUserSuccess,
  ],
  (
    getAllWorkspacesSuccess: boolean,
    getAdminWorkspacesSuccess: boolean,
    getArchivedWorkspacesSuccess: boolean,
    archiveWorkspaceSuccess: boolean,
    getWorkspaceByIdSuccess: boolean,
    createWorkspaceSuccess: boolean,
    updateWorkspaceSuccess: boolean,
    deleteWorkspaceSuccess: boolean,
    switchWorkspaceSuccess: boolean,
    removeUserSuccess: boolean
  ) => ({
    getAllWorkspacesSuccess,
    getAdminWorkspacesSuccess,
    getArchivedWorkspacesSuccess,
    archiveWorkspaceSuccess,
    getWorkspaceByIdSuccess,
    createWorkspaceSuccess,
    updateWorkspaceSuccess,
    deleteWorkspaceSuccess,
    switchWorkspaceSuccess,
    removeUserSuccess,
    anySuccess:
      getAllWorkspacesSuccess ||
      getAdminWorkspacesSuccess ||
      getArchivedWorkspacesSuccess ||
      archiveWorkspaceSuccess ||
      getWorkspaceByIdSuccess ||
      createWorkspaceSuccess ||
      updateWorkspaceSuccess ||
      deleteWorkspaceSuccess ||
      switchWorkspaceSuccess ||
      removeUserSuccess,
  })
);
